import {
  CatalogUIPolicy,
  CatalogUIPolicyAction,
} from '../models/catalog-form';

// Per-field state after evaluating every policy that touches it. `undefined`
// on a property means "no policy explicitly set it" — consumers merge with
// their own baseline. Matches the guide's delta model (§13 rule 7).
export interface UIPolicyFieldState {
  visible?: boolean;
  mandatory?: boolean;
  readOnly?: boolean;
}

export type UIPolicyDelta = Record<string, UIPolicyFieldState>;
export type UIPolicyValues = Record<string, unknown>;

// Ported from client-sdk/UIPolicyEvaluator.js with two intentional differences
// from the SDK (both align this port with the guide §6.3, which is authoritative):
//   1. Adds `IN` and `NOT IN` operators (guide §13 rule 4 — multi-select
//      dropdowns depend on them).
//   2. `reverseIfFalse` applies the OPPOSITE of the action value, not
//      "restore original state" (guide §13 rule 3). Matches
//      `collectStaticOverrides` in catalog-form.component.ts.
export class UIPolicyEvaluator {
  private readonly _policies: readonly CatalogUIPolicy[];

  constructor(policies: readonly CatalogUIPolicy[] = []) {
    this._policies = [...policies].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
  }

  // Returns the delta only — no field appears unless a policy touched it.
  evaluate(values: UIPolicyValues): UIPolicyDelta {
    const delta: UIPolicyDelta = {};
    for (const policy of this._policies) {
      const conditionMet = this._evaluateCondition(policy.conditions, values);
      for (const action of policy.actions ?? []) {
        const field = action.variableName;
        if (!field) continue;
        if (conditionMet) {
          this._applyAction(delta, field, action);
        } else if (policy.reverseIfFalse === true) {
          this._reverseAction(delta, field, action);
        }
      }
    }
    return delta;
  }

  // Supports the SN encoded-query syntax: `^` = AND, `^OR` or `^NQ` = OR
  // group boundary. Empty conditions are treated as "always true" (guide §6).
  private _evaluateCondition(
    conditionStr: string | null | undefined,
    values: UIPolicyValues,
  ): boolean {
    if (!conditionStr) return true;
    const orGroups = conditionStr.split(/\^NQ|\^OR/);
    return orGroups.some((group) => this._evaluateAndGroup(group, values));
  }

  private _evaluateAndGroup(group: string, values: UIPolicyValues): boolean {
    const conditions = group.split('^').filter(Boolean);
    return conditions.every((cond) =>
      this._evaluateSingleCondition(cond, values),
    );
  }

  // Order is longest-first — a shorter operator that is a substring of a
  // longer one (e.g. `IN` inside `CONTAINS`, `<` inside `<=`) must never be
  // tried before the longer one, or the parser misdetects the field/value
  // split.
  private _evaluateSingleCondition(
    cond: string,
    values: UIPolicyValues,
  ): boolean {
    for (const [op, compare] of OPERATOR_TABLE) {
      const idx = cond.indexOf(op);
      if (idx === -1) continue;
      const field = cond.substring(0, idx).trim();
      const expected = cond.substring(idx + op.length).trim();
      // Form values are always primitives (string/number/boolean) — no object
      // stringification path in practice. NOSONAR: typescript:S6551.
      const actual = String(values[field] ?? ''); // NOSONAR
      return compare(actual, expected);
    }
    return false;
  }

  private _applyAction(
    delta: UIPolicyDelta,
    field: string,
    action: CatalogUIPolicyAction,
  ): void {
    const patch = ensureField(delta, field);
    if (action.visible === 'true') patch.visible = true;
    else if (action.visible === 'false') patch.visible = false;
    if (action.mandatory === 'true') patch.mandatory = true;
    else if (action.mandatory === 'false') patch.mandatory = false;
    if (action.readOnly === 'true') patch.readOnly = true;
    else if (action.readOnly === 'false') patch.readOnly = false;
  }

  // Guide §13 rule 3: apply the opposite of the action value, not "restore
  // original". Only reverse dimensions the action explicitly set (empty
  // string means "no change" and must not be reversed).
  private _reverseAction(
    delta: UIPolicyDelta,
    field: string,
    action: CatalogUIPolicyAction,
  ): void {
    const patch = ensureField(delta, field);
    if (action.visible === 'true') patch.visible = false;
    else if (action.visible === 'false') patch.visible = true;
    if (action.mandatory === 'true') patch.mandatory = false;
    else if (action.mandatory === 'false') patch.mandatory = true;
    if (action.readOnly === 'true') patch.readOnly = false;
    else if (action.readOnly === 'false') patch.readOnly = true;
  }
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Operator name → binary comparator. Order (see call site) matters: longest
// operators first so `IN` doesn't shadow `CONTAINS`, `<` doesn't shadow `<=`.
const OPERATOR_TABLE: ReadonlyArray<
  readonly [string, (actual: string, expected: string) => boolean]
> = [
  ['ISNOTEMPTY', (a) => a !== ''],
  ['STARTSWITH', (a, e) => a.startsWith(e)],
  ['CONTAINS', (a, e) => a.includes(e)],
  ['ENDSWITH', (a, e) => a.endsWith(e)],
  ['ISEMPTY', (a) => a === ''],
  ['NOT IN', (a, e) => !splitCsv(e).includes(a)],
  ['<=', (a, e) => Number.parseFloat(a) <= Number.parseFloat(e)],
  ['>=', (a, e) => Number.parseFloat(a) >= Number.parseFloat(e)],
  ['!=', (a, e) => a !== e],
  ['IN', (a, e) => splitCsv(e).includes(a)],
  ['=', (a, e) => a === e],
  ['<', (a, e) => Number.parseFloat(a) < Number.parseFloat(e)],
  ['>', (a, e) => Number.parseFloat(a) > Number.parseFloat(e)],
];

function ensureField(
  delta: UIPolicyDelta,
  field: string,
): UIPolicyFieldState {
  if (!delta[field]) delta[field] = {};
  return delta[field];
}
